import { AWS_S3_URL } from "../../../../../utils/constants";
import {
  amountInWords,
  born,
  daysBetween,
  documentTypeLabel,
  employed,
  concerned,
  frAmount,
  frDate,
  shortCivility,
} from "../../../utils/documents";
import "./print.css";

/**
 * Renders an issued document as an A4 sheet, entirely from the snapshot frozen
 * at issue time. Nothing here reads the live employee or company record, so a
 * reprint years later is identical to the original.
 *
 * The body is French by design: an Algerian bank, consulate or administration
 * will not accept an attestation in another language.
 */

function Identity({ employee }) {
  return (
    <div className="doc-identity">
      <div>
        <span className="doc-label">Nom et prénom :</span>
        <strong>{(employee.lastname || '').toUpperCase()} {employee.firstname}</strong>
      </div>
      {employee.birthdate && (
        <div>
          <span className="doc-label">{born(employee.gender) === 'née' ? 'Née le' : 'Né(e) le'} :</span>
          {frDate(employee.birthdate)}
          {employee.birthplace ? ` à ${employee.birthplace}` : ''}
        </div>
      )}
      {employee.address && (
        <div>
          <span className="doc-label">Demeurant à :</span>
          {[employee.address, employee.city].filter(Boolean).join(', ')}
        </div>
      )}
      {employee.social_security_number && (
        <div>
          <span className="doc-label">N° de sécurité sociale :</span>
          {employee.social_security_number}
        </div>
      )}
    </div>
  );
}

function Preamble({ signatoryName, signatoryTitle, company, verb }) {
  return (
    <p>
      Je soussigné{signatoryName ? '' : '(e)'}
      {signatoryName ? <> <strong>{signatoryName}</strong></> : ''}, {signatoryTitle} de
      la société <strong>{company.legal_name || company.display_name}</strong>, {verb} :
    </p>
  );
}

function AttestationTravail({ doc, employee, company }) {
  return (
    <>
      <Preamble
        signatoryName={doc.signatory_name}
        signatoryTitle={doc.signatory_title}
        company={company}
        verb="atteste par la présente que"
      />
      <Identity employee={employee} />
      <p>
        Est {employed(employee.gender)} au sein de notre entreprise depuis le{' '}
        <strong>{frDate(employee.contract_start_date)}</strong>, en qualité de{' '}
        <strong>{employee.job_title || '—'}</strong>
        {employee.department ? <>, au sein du département {employee.department}</> : ''}.
      </p>
      <p>
        La présente attestation est délivrée à {concerned(employee.gender)} pour servir
        et valoir ce que de droit.
      </p>
    </>
  );
}

function CertificatTravail({ doc, employee, company, payload }) {
  return (
    <>
      <Preamble
        signatoryName={doc.signatory_name}
        signatoryTitle={doc.signatory_title}
        company={company}
        verb="certifie que"
      />
      <Identity employee={employee} />
      <p>
        A été {employed(employee.gender)} au sein de notre entreprise du{' '}
        <strong>{frDate(employee.contract_start_date)}</strong> au{' '}
        <strong>{frDate(payload.end_date)}</strong>, en qualité de{' '}
        <strong>{payload.positions_held || employee.job_title || '—'}</strong>.
      </p>
      <p>
        {concerned(employee.gender).charAt(0).toUpperCase() + concerned(employee.gender).slice(1)}{' '}
        quitte l'entreprise libre de tout engagement.
      </p>
      <p>
        Le présent certificat est délivré conformément aux dispositions de l'article 67 de la
        loi n° 90-11 du 21 avril 1990, modifiée et complétée, relative aux relations de travail,
        pour servir et valoir ce que de droit.
      </p>
    </>
  );
}

function AttestationSalaire({ doc, employee, company, payload, salary }) {
  const allowances = salary?.allowances ?? [];
  const deductions = salary?.deductions ?? [];

  return (
    <>
      <Preamble
        signatoryName={doc.signatory_name}
        signatoryTitle={doc.signatory_title}
        company={company}
        verb="atteste que"
      />
      <Identity employee={employee} />
      <p>
        Est {employed(employee.gender)} au sein de notre entreprise depuis le{' '}
        <strong>{frDate(employee.contract_start_date)}</strong>, en qualité de{' '}
        <strong>{employee.job_title || '—'}</strong>, et perçoit la rémunération mensuelle
        détaillée ci-après :
      </p>

      <table className="doc-table">
        <tbody>
          <tr>
            <td>Salaire de base</td>
            <td>{frAmount(salary?.basic_salary)} DA</td>
          </tr>
          {allowances.map((line, i) => (
            <tr key={`a-${i}`}>
              <td>{line.name}</td>
              <td>{frAmount(line.amount)} DA</td>
            </tr>
          ))}
          <tr className="doc-table-sub">
            <td>Salaire brut mensuel</td>
            <td>{frAmount(salary?.gross_salary)} DA</td>
          </tr>
          {deductions.map((line, i) => (
            <tr key={`d-${i}`}>
              <td>{line.name}</td>
              <td>- {frAmount(line.amount)} DA</td>
            </tr>
          ))}
          <tr className="doc-table-total">
            <td>Salaire net mensuel</td>
            <td>{frAmount(salary?.net_salary)} DA</td>
          </tr>
        </tbody>
      </table>

      <p>
        Soit, en toutes lettres : <strong>{amountInWords(salary?.net_salary)}</strong>.
      </p>
      <p>
        La présente attestation est délivrée à {concerned(employee.gender)}
        {payload.purpose ? ` en vue de ${payload.purpose}` : ''} pour servir et valoir
        ce que de droit.
      </p>
    </>
  );
}

function OrdreMission({ doc, employee, company, payload }) {
  const days = daysBetween(payload.departure_date, payload.return_date);

  return (
    <>
      <Preamble
        signatoryName={doc.signatory_name}
        signatoryTitle={doc.signatory_title}
        company={company}
        verb="donne ordre à"
      />
      <div className="doc-identity">
        <div>
          <span className="doc-label">Nom et prénom :</span>
          <strong>{(employee.lastname || '').toUpperCase()} {employee.firstname}</strong>
        </div>
        <div>
          <span className="doc-label">Fonction :</span>
          {employee.job_title || '—'}
        </div>
      </div>

      <p>
        De se rendre en mission à <strong>{payload.destination}</strong>.
      </p>

      <div className="doc-identity">
        <div><span className="doc-label">Objet de la mission :</span>{payload.purpose}</div>
        {payload.transport && (
          <div><span className="doc-label">Moyen de transport :</span>{payload.transport}</div>
        )}
        <div><span className="doc-label">Date de départ :</span>{frDate(payload.departure_date)}</div>
        <div><span className="doc-label">Date de retour :</span>{frDate(payload.return_date)}</div>
        <div><span className="doc-label">Durée :</span>{days} jour{days > 1 ? 's' : ''}</div>
      </div>

      {payload.expenses_covered && (
        <p>
          Les frais de mission (transport, hébergement et restauration) sont à la charge
          de l'entreprise.
        </p>
      )}
      <p>
        Les autorités civiles et militaires sont priées de faciliter l'accomplissement de
        la mission de {concerned(employee.gender)}.
      </p>
    </>
  );
}

function DecisionAffectation({ doc, employee, company, payload }) {
  return (
    <>
      <p>{doc.signatory_title} de la société <strong>{company.legal_name || company.display_name}</strong>,</p>

      <p className="doc-visa">
        Vu la loi n° 90-11 du 21 avril 1990, modifiée et complétée, relative aux relations
        de travail ;
      </p>
      <p className="doc-visa">Vu le règlement intérieur de l'entreprise ;</p>
      <p className="doc-visa">Vu le contrat de travail de {concerned(employee.gender)} ;</p>
      {payload.reason && <p className="doc-visa">Considérant {payload.reason} ;</p>}

      <div className="doc-decides">DÉCIDE</div>

      <p className="doc-article">
        <strong>Article 1<sup>er</sup> :</strong>{' '}
        {shortCivility(employee.gender)} <strong>{(employee.lastname || '').toUpperCase()} {employee.firstname}</strong>{' '}
        est {employee.gender === 'female' ? 'affectée' : 'affecté(e)'} au poste de{' '}
        <strong>{payload.new_position}</strong>
        {payload.new_department ? <>, au sein du département {payload.new_department}</> : ''}, à
        compter du <strong>{frDate(payload.effective_date)}</strong>.
      </p>
      <p className="doc-article">
        <strong>Article 2 :</strong> {concerned(employee.gender).charAt(0).toUpperCase() + concerned(employee.gender).slice(1)}{' '}
        exercera ses fonctions conformément aux attributions liées à ce poste.
      </p>
      <p className="doc-article">
        <strong>Article 3 :</strong> La présente décision prend effet à compter de la date
        sus-indiquée et sera notifiée à {concerned(employee.gender)} et versée à son dossier.
      </p>
    </>
  );
}

function AutorisationHeures({ doc, employee, company, payload }) {
  return (
    <>
      <Preamble
        signatoryName={doc.signatory_name}
        signatoryTitle={doc.signatory_title}
        company={company}
        verb="autorise"
      />
      <div className="doc-identity">
        <div>
          <span className="doc-label">Nom et prénom :</span>
          <strong>{(employee.lastname || '').toUpperCase()} {employee.firstname}</strong>
        </div>
        <div>
          <span className="doc-label">Fonction :</span>
          {employee.job_title || '—'}
        </div>
      </div>

      <p>
        À effectuer <strong>{payload.hours}</strong> heure{Number(payload.hours) > 1 ? 's' : ''}{' '}
        supplémentaire{Number(payload.hours) > 1 ? 's' : ''} durant la période du{' '}
        <strong>{frDate(payload.from_date)}</strong> au <strong>{frDate(payload.to_date)}</strong>.
      </p>

      {payload.reason && <p>Motif : {payload.reason}</p>}

      <p>
        Ces heures seront rémunérées conformément aux dispositions des articles 31 et 32 de
        la loi n° 90-11 du 21 avril 1990, modifiée et complétée, relative aux relations
        de travail.
      </p>
    </>
  );
}

const BODIES = {
  attestation_travail: AttestationTravail,
  certificat_travail: CertificatTravail,
  attestation_salaire: AttestationSalaire,
  ordre_mission: OrdreMission,
  decision_affectation: DecisionAffectation,
  autorisation_heures_supplementaires: AutorisationHeures,
};

export default function DocumentSheet({ document }) {
  if (!document) return null;

  const snapshot = document.snapshot ?? {};
  const company = snapshot.company ?? {};
  const employee = snapshot.employee ?? {};
  const payload = document.payload ?? {};
  const Body = BODIES[document.type];

  const identifiers = [
    company.rc ? `RC : ${company.rc}` : null,
    company.nif ? `NIF : ${company.nif}` : null,
    company.nis ? `NIS : ${company.nis}` : null,
    company.cnas ? `CNAS : ${company.cnas}` : null,
  ].filter(Boolean);

  return (
    <div className="doc-sheet">
      <div className="doc-letterhead">
        <div>
          <div className="doc-company-name">{company.legal_name || company.display_name || '—'}</div>
          <div className="doc-company-meta">
            {[company.address, company.city, company.country].filter(Boolean).join(', ')}
            {company.phone && <><br />Tél : {company.phone}</>}
            {company.email && <> — Email : {company.email}</>}
            {identifiers.length > 0 && <><br />{identifiers.join(' — ')}</>}
          </div>
        </div>
        {company.logo_path && (
          <img className="doc-logo" src={AWS_S3_URL + company.logo_path} alt="" />
        )}
      </div>

      <div className="doc-reference">
        <span>Réf. : N° {document.reference}</span>
        <span>
          {document.issue_place ? `${document.issue_place}, le ` : 'Le '}
          {frDate(document.issue_date)}
        </span>
      </div>

      <div className="doc-title">{documentTypeLabel(document.type)}</div>

      <div className="doc-body">
        {Body ? (
          <Body
            doc={document}
            employee={employee}
            company={company}
            payload={payload}
            salary={snapshot.salary}
          />
        ) : (
          <p>Type de document inconnu.</p>
        )}
      </div>

      <div className="doc-signature">
        <div className="doc-signature-place">
          Fait à {document.issue_place || company.city || '—'}, le {frDate(document.issue_date)}
        </div>
        <div className="doc-signature-title">{document.signatory_title}</div>
        {document.signatory_name && <div>{document.signatory_name}</div>}
        {document.status === 'cancelled' && (
          <div className="doc-cancelled-stamp">ANNULÉ</div>
        )}
      </div>
    </div>
  );
}
